import { Suspense, useEffect, useMemo, useRef, useState, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import buildTheme from '@theme/mui.js';
import { ColorSchemeProvider, useColorScheme } from '@theme/ColorSchemeContext.jsx';
import AuthProvider, { useAuth } from '@core/auth/AuthProvider.jsx';
import OfferProvider, { useOffer } from '@core/offerContext.jsx';
import { loadModules } from '@core/moduleLoader.js';
import { initPerfBaseline } from '@core/perf/baseline.js';
import AppShell from '@layouts/AppShell.jsx';
import AuthLayout from '@layouts/AuthLayout.jsx';
import PublicLayout from '@layouts/PublicLayout.jsx';
import NotFound from '@pages/NotFound.jsx';
import Forbidden from '@pages/Forbidden.jsx';
import NotificationHost from '@shared/components/NotificationHost.jsx';
import ScrollToTop from '@shared/components/ScrollToTop.jsx';
import { useNotify } from '@shared/hooks/useNotify.js';
import { onApiErrorEvent } from '@core/api.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';

const MarketingShell = lazy(() => import('@marketing/MarketingShell.jsx'));

const AccueilPage = lazy(() => import('@marketing/pages/AccueilPage.jsx'));
const ContactPage = lazy(() => import('@marketing/pages/ContactPage.jsx'));
const ServicesPage = lazy(() => import('@marketing/pages/ServicesPage.jsx'));
const DevisPage = lazy(() => import('@marketing/pages/DevisPage.jsx'));
const ProposPage = lazy(() => import('@marketing/pages/ProposPage.jsx'));
const DemandePage = lazy(() => import('@marketing/pages/DemandePage.jsx'));
const MentionsLegalesPage = lazy(() => import('@marketing/pages/MentionsLegalesPage.jsx'));
const PolitiqueConfidentialitePage = lazy(() => import('@marketing/pages/PolitiqueConfidentialitePage.jsx'));
const DesignSystemPreview = lazy(() => import('@theme/DesignSystemPreview.jsx'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function BootSpinner() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'var(--color-surface, #faf8f6)',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function Shell() {
  const { isAuthenticated, hasPermission } = useAuth();
  const { modules, manifest, features } = useOffer();
  const [resolvedModules, setResolvedModules] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadModules(modules).then((mods) => mounted && setResolvedModules(mods));
    return () => {
      mounted = false;
    };
  }, [modules]);

  if (resolvedModules === null) return <BootSpinner />;

  const hasFeature = (flag) => !flag || features?.[flag] !== false;
  const routes = resolvedModules
    .flatMap((m) => m.routes ?? [])
    .filter((r) => (!r.permission || hasPermission(r.permission)) && hasFeature(r.featureFlag));
  const menu = resolvedModules
    .flatMap((m) => m.menu ?? [])
    .filter((item) => (!item.permission || hasPermission(item.permission)) && hasFeature(item.featureFlag))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const dashboardRoute = routes.find((r) => r.path === '/dashboard');
  const appHome = dashboardRoute?.path ?? routes[0]?.path ?? '/403';

  return (
    <Routes>
      <Route
        path="/design-system"
        element={
          <Suspense fallback={<BootSpinner />}>
            <DesignSystemPreview />
          </Suspense>
        }
      />

      <Route element={<AuthLayout />}>
        {resolvedModules
          .flatMap((m) => m.publicRoutes ?? [])
          .map(({ path, element: Element }) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<BootSpinner />}>
                  <Element />
                </Suspense>
              }
            />
          ))}
      </Route>

      <Route
        element={
          isAuthenticated ? (
            <AppShell menu={menu} manifest={manifest} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        {routes.map(({ path, element: Element, permission }) => (
          <Route
            key={path}
            path={path}
            element={
              permission && !hasPermission(permission) ? (
                <Navigate to="/403" replace />
              ) : (
                <Suspense fallback={<BootSpinner />}>
                  <Element />
                </Suspense>
              )
            }
          />
        ))}
      </Route>

      <Route element={<PublicLayout />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                {isAuthenticated ? <Navigate to={appHome} replace /> : <AccueilPage />}
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <ContactPage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/services"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <ServicesPage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/devis"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <DevisPage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/a-propos"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <ProposPage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/mentions-legales"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <MentionsLegalesPage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/politique-de-confidentialite"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <PolitiqueConfidentialitePage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route
          path="/demande-de-reservation"
          element={
            <Suspense fallback={<BootSpinner />}>
              <MarketingShell>
                <DemandePage />
              </MarketingShell>
            </Suspense>
          }
        />
        <Route path="/403" element={<Forbidden />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    initPerfBaseline();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <OfferProvider>
        <ColorSchemeProvider>
          <ThemedApp />
        </ColorSchemeProvider>
      </OfferProvider>
    </QueryClientProvider>
  );
}

function ThemedApp() {
  const { ready, theme: themeInput } = useOffer();
  const { resolved } = useColorScheme();
  const notify = useNotify();
  const perfLogState = useRef({});

  const theme = useMemo(
    () => buildTheme(themeInput || {}, { mode: resolved }),
    [themeInput, resolved],
  );

  useEffect(() => {
    const root = document.documentElement;
    const t = themeInput || {};
    if (t.primary) root.style.setProperty('--color-brand', t.primary);
    if (t.secondary) root.style.setProperty('--color-secondary', t.secondary);
  }, [themeInput]);

  useEffect(() => {
    if (!ready) return undefined;

    const onWarning = (event) => notify.warn(event?.detail?.message || 'Avertissement réseau.');
    const onMetric = (event) => {
      if (import.meta.env.DEV) {
        const detail = event?.detail ?? {};
        const metric = detail.metric;
        const value = Number(detail.value);
        if (!metric || !Number.isFinite(value)) return;

        const now = Date.now();
        const prev = perfLogState.current[metric];
        const shouldLog = !prev || value > prev.value || (now - prev.at) > 10_000;
        if (!shouldLog) return;

        perfLogState.current[metric] = { value, at: now };
        // eslint-disable-next-line no-console
        console.debug('[perf]', { metric, value });
      }
    };
    const detachApiError = onApiErrorEvent((error) => {
      if (error?.response?.status === 429) {
        notify.warn(apiErrorMessage(error, 'Trop de requêtes.'));
      }
    });

    window.addEventListener('starter:api-warning', onWarning);
    window.addEventListener('starter:perf-metric', onMetric);

    return () => {
      window.removeEventListener('starter:api-warning', onWarning);
      window.removeEventListener('starter:perf-metric', onMetric);
      if (typeof detachApiError === 'function') detachApiError();
    };
  }, [notify, ready]);

  if (!ready) return <BootSpinner />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <Shell />
          <NotificationHost />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
